import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';

interface FeedbackData {
  overallRating: number;
  easeOfUse: number;
  designRating: number;
  wouldRecommend: boolean | null;
  improvements: string;
  additionalComments: string;
}

const initialFeedback: FeedbackData = {
  overallRating: 0,
  easeOfUse: 0,
  designRating: 0,
  wouldRecommend: null,
  improvements: '',
  additionalComments: '',
};

export default function FeedbackWidget() {
  const { t, i18n } = useTranslation('feedback');
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackData>(initialFeedback);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRTL = i18n.dir() === 'rtl';
  const revealed = useRevealOnScroll();

  const handleRatingClick = (field: 'overallRating' | 'easeOfUse' | 'designRating', value: number) => {
    setFeedback(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate at least one rating is provided
    if (feedback.overallRating === 0) {
      setError(t('feedback.validation.ratingRequired'));
      setIsSubmitting(false);
      return;
    }

    try {
      // Send to the same API as contact form using URL-encoded format
      const response = await fetch('https://readdy.ai/api/form/d34sogfs3d54c51mb250', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          type: 'Website Feedback',
          overallRating: String(feedback.overallRating),
          easeOfUse: String(feedback.easeOfUse),
          designRating: String(feedback.designRating),
          wouldRecommend: feedback.wouldRecommend === null ? 'Not answered' : feedback.wouldRecommend ? 'Yes' : 'No',
          improvements: feedback.improvements || 'No suggestions provided',
          additionalComments: feedback.additionalComments || 'No additional comments',
          language: i18n.language,
          submittedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
        setFeedback(initialFeedback);
      }, 3000);
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError(t('feedback.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating = ({
    value,
    onChange,
    label
  }: {
    value: number;
    onChange: (v: number) => void;
    label: string;
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className={`flex gap-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`text-2xl transition-all duration-200 hover:scale-110 ${
              star <= value ? 'text-amber-400' : 'text-slate-300 hover:text-amber-200'
            }`}
            aria-label={`${star} stars`}
          >
            <i className={star <= value ? 'ri-star-fill' : 'ri-star-line'}></i>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Feedback Button — hidden on first paint, slides in once the visitor
          starts scrolling (sits stacked above the live-chat button). */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed z-40 flex items-center gap-2 rounded-full bg-[#004D40] px-4 py-3 text-sm font-medium text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
          isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'
        } bottom-[calc(6rem+var(--pv-float-offset,0px))] sm:bottom-[calc(7rem+var(--pv-float-offset,0px))] ${
          revealed
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-hidden={!revealed}
        tabIndex={revealed ? 0 : -1}
        aria-label={t('feedback.buttonLabel')}
      >
        <i className="ri-feedback-line text-lg"></i>
        <span className="hidden sm:inline">{t('feedback.buttonLabel')}</span>
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isSubmitting && setIsOpen(false)}
          />

          {/* Modal */}
          <div
            className={`relative w-full max-w-lg max-h-[85vh] overflow-y-auto overscroll-contain rounded-3xl bg-white shadow-2xl ${
              isRTL ? 'text-right' : 'text-left'
            }`}
            style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,77,64,0.3) transparent' }}
            dir={i18n.dir()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-[#004D40] px-6 py-4 text-white rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <i className="ri-feedback-line text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold">{t('feedback.title')}</h2>
                  <p className="text-xs text-white/80">{t('feedback.subtitle')}</p>
                </div>
              </div>
              <button
                onClick={() => !isSubmitting && setIsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition hover:bg-white/30"
                disabled={isSubmitting}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {isSubmitted ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]">
                    <i className="ri-check-line text-3xl text-[#004D40]"></i>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-slate-900">{t('feedback.success.title')}</h3>
                  <p className="text-slate-600">{t('feedback.success.message')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Overall Experience */}
                  <StarRating
                    value={feedback.overallRating}
                    onChange={(v) => handleRatingClick('overallRating', v)}
                    label={t('feedback.questions.overall')}
                  />

                  {/* Ease of Use */}
                  <StarRating
                    value={feedback.easeOfUse}
                    onChange={(v) => handleRatingClick('easeOfUse', v)}
                    label={t('feedback.questions.easeOfUse')}
                  />

                  {/* Design Rating */}
                  <StarRating
                    value={feedback.designRating}
                    onChange={(v) => handleRatingClick('designRating', v)}
                    label={t('feedback.questions.design')}
                  />

                  {/* Would Recommend */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      {t('feedback.questions.recommend')}
                    </label>
                    <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <button
                        type="button"
                        onClick={() => setFeedback(prev => ({ ...prev, wouldRecommend: true }))}
                        className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition ${
                          feedback.wouldRecommend === true
                            ? 'bg-[#004D40] text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <i className="ri-thumb-up-line"></i>
                        {t('feedback.options.yes')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFeedback(prev => ({ ...prev, wouldRecommend: false }))}
                        className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition ${
                          feedback.wouldRecommend === false
                            ? 'bg-rose-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <i className="ri-thumb-down-line"></i>
                        {t('feedback.options.no')}
                      </button>
                    </div>
                  </div>

                  {/* Improvements Suggestion */}
                  <div className="space-y-2">
                    <label htmlFor="improvements" className="block text-sm font-medium text-slate-700">
                      {t('feedback.questions.improvements')}
                    </label>
                    <textarea
                      id="improvements"
                      value={feedback.improvements}
                      onChange={(e) => setFeedback(prev => ({ ...prev, improvements: e.target.value }))}
                      placeholder={t('feedback.placeholders.improvements')}
                      rows={3}
                      className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-[#004D40] focus:outline-none focus:ring-2 focus:ring-[#F2F8F7]"
                    />
                  </div>

                  {/* Additional Comments */}
                  <div className="space-y-2">
                    <label htmlFor="additionalComments" className="block text-sm font-medium text-slate-700">
                      {t('feedback.questions.additional')}
                    </label>
                    <textarea
                      id="additionalComments"
                      value={feedback.additionalComments}
                      onChange={(e) => setFeedback(prev => ({ ...prev, additionalComments: e.target.value }))}
                      placeholder={t('feedback.placeholders.additional')}
                      rows={3}
                      className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-[#004D40] focus:outline-none focus:ring-2 focus:ring-[#F2F8F7]"
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      <i className="ri-error-warning-line text-lg"></i>
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-xl bg-[#004D40] py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t('feedback.submitting')}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-line"></i>
                        {t('feedback.submit')}
                      </span>
                    )}
                  </button>

                  {/* Privacy Note */}
                  <p className="text-center text-xs text-slate-500">
                    {t('feedback.privacy')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
